import React, { useEffect, useState } from 'react';
import {
  Users,
  Briefcase,
  Calendar,
  Video,
  ArrowRight,
  ChevronDown,
  MessageCircle,
  Check,
  Rocket,
  Globe,
  Shuffle,
} from 'lucide-react';
import { trackPointer, MediaPlaceholder } from './UI';
import { getValidUrl, hasCustomImage, truncateText } from '../utils/Utils';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3005';

const HERO_IMAGE =
  'https://autohebdosport.com/images/coches/salones/2025/autopia/segundo-previo/autopia-2025.jpg';

const SECTIONS = [
  {
    icon: Users,
    title: 'Networking',
    text: 'Conecta con líderes y profesionales de alto nivel del sector.',
    link: '/networking',
  },
  {
    icon: Briefcase,
    title: 'Proyectos',
    text: 'Accede a oportunidades de inversión y colaboración estratégica.',
    link: '/projects',
  },
  {
    icon: Calendar,
    title: 'Eventos',
    text: 'Asiste a encuentros exclusivos y sesiones de alto valor añadido.',
    link: '/events',
  },
  {
    icon: Video,
    title: 'Reuniones online',
    text: 'Coordina encuentros y discusiones de impacto con expertos.',
    link: '/meetings',
  },
];

const PLATFORM = [
  'Directorio de miembros con sus empresas y proyectos',
  'Fichas de proyecto por categoría, con web y equipo',
  'Eventos con lugar, fecha y lista de asistentes',
  'Reuniones online con enlace de acceso directo',
  'Mensajes a otros miembros sin salir de la plataforma',
  'Perfil editable: avatar, portada, bio y enlaces',
];

/** Revela los hijos al entrar en pantalla, con escalonado. */
function useReveal() {
  // Ref de callback en vez de una ref normal: hay bloques que aparecen más
  // tarde (los que esperan a una petición). Con useRef el efecto corría cuando
  // todavía no existía el nodo, el observer se quedaba sin nada que mirar y el
  // contenido no se revelaba nunca.
  const [node, setNode] = useState(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!node || shown) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [node, shown]);

  return [setNode, shown];
}

/** Trae los proyectos publicados y deja uno al azar, con opción a cambiarlo. */
function useRandomProject() {
  const [pool, setPool] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const res = await fetch(`${API}/project/`);
        const data = await res.json();
        if (!alive) return;
        // En la home solo caben los que ya ha validado el club
        const visibles = (Array.isArray(data) ? data : []).filter((p) => p.accepted);
        setPool(visibles);
        setProject(visibles[Math.floor(Math.random() * visibles.length)] || null);
      } catch (err) {
        console.error('Error cargando proyectos:', err);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // Evita repetir el que ya está en pantalla mientras haya alternativas
  const shuffle = () => {
    const otros = pool.filter((p) => p._id !== project?._id);
    const lista = otros.length ? otros : pool;
    if (lista.length) setProject(lista[Math.floor(Math.random() * lista.length)]);
  };

  return { project, loading, shuffle, canShuffle: pool.length > 1 };
}

function HomeComponent() {
  const [offset, setOffset] = useState(0);
  const [gridRef, gridShown] = useReveal();
  const [platformRef, platformShown] = useReveal();
  const [spotRef, spotShown] = useReveal();
  const { project, loading: projectLoading, shuffle, canShuffle } = useRandomProject();

  // Parallax suave del fondo del hero.
  // El scroll dispara muchísimo: sin agrupar con rAF, cada evento provocaba
  // un render completo de la home solo para mover un fondo.
  //
  // En móvil no se mueve: mover una imagen a pantalla completa en cada scroll
  // va a tirones en el teléfono, y además la barra del navegador al aparecer y
  // desaparecer ya cambia el alto, con lo que el desplazamiento descuadra el
  // hero. Se respeta también quien pide menos movimiento.
  useEffect(() => {
    const quieto = window.matchMedia(
      '(max-width: 900px), (prefers-reduced-motion: reduce)'
    );
    if (quieto.matches) {
      setOffset(0);
      return;
    }

    let frame = null;
    const onScroll = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        setOffset(window.scrollY);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div className="dyd-home">
      {/* ─────────── HERO ─────────── */}
      <header className="dyd-hero">
        <div
          className="dyd-hero-bg"
          style={{
            backgroundImage: `url(${HERO_IMAGE})`,
            transform: `translate3d(0, ${offset * 0.28}px, 0) scale(1.12)`,
          }}
        />
        <div className="dyd-hero-veil" />
        <div className="dyd-hero-grid" />

        <div className="dyd-hero-content">
          <span className="dyd-eyebrow dyd-rise" style={{ '--i': 0 }}>
            Club privado · Automoción &amp; movilidad
          </span>

          <h1 className="dyd-rise" style={{ '--i': 1 }}>
            DRIVINGYOURDREAM
            <br />
            <span className="dyd-gradient-text">CLUB</span>
          </h1>

          <p className="dyd-rise" style={{ '--i': 2 }}>
            Conectamos visionarios, proyectos y oportunidades en el sector automoción
            y movilidad urbana.
            <br />
            <strong>Donde la excelencia se encuentra con la acción.</strong>
          </p>

          <div className="dyd-hero-cta dyd-rise" style={{ '--i': 3 }}>
            <a
              href="https://chat.whatsapp.com/LNCb1HED9sS5eQEgI3nleJ"
              target="_blank"
              rel="noopener noreferrer"
              className="dyd-btn dyd-btn-lg dyd-whatsapp"
            >
              <MessageCircle size={19} strokeWidth={2.2} />
              Únete al grupo
            </a>
            <a href="/networking" className="dyd-btn dyd-btn-ghost dyd-btn-lg">
              Explorar la comunidad
              <ArrowRight size={18} className="dyd-arrow" />
            </a>
          </div>
        </div>

        <div className="dyd-scroll-hint" aria-hidden="true">
          <ChevronDown size={22} />
        </div>
      </header>

      {/* ─────────── ECOSISTEMA ─────────── */}
      <section className="dyd-eco">
        <div className="dyd-eco-inner">
          <div className="dyd-eco-head">
            <span className="dyd-eyebrow">El ecosistema</span>
            <h2>
              Todo lo que el club <span className="dyd-gradient-text">pone a tu alcance</span>
            </h2>
          </div>

          <div className="dyd-eco-grid" ref={gridRef}>
            {SECTIONS.map(({ icon: Icon, title, text, link }, idx) => (
              <a
                key={link}
                href={link}
                className={`dyd-card dyd-eco-card ${gridShown ? 'dyd-rise' : 'is-hidden'}`}
                style={{ '--i': idx }}
                onMouseMove={trackPointer}
              >
                <div className="dyd-card-body">
                  <span className="dyd-eco-icon">
                    <Icon size={26} strokeWidth={1.8} />
                  </span>
                  <h3 className="dyd-card-title">{title}</h3>
                  <p className="dyd-card-text">{text}</p>
                  <span className="dyd-eco-link">
                    Acceder <ArrowRight size={15} className="dyd-arrow" />
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── CONOCE UN PROYECTO ─────────── */}
      {(projectLoading || project) && (
        <section className="dyd-spot">
          <div className="dyd-spot-inner">
            <div className="dyd-spot-head">
              <div>
                <span className="dyd-eyebrow">Conoce un proyecto</span>
                <h2>
                  Lo que se está <span className="dyd-gradient-text">construyendo aquí</span>
                </h2>
              </div>
              {canShuffle && (
                <button type="button" className="dyd-btn dyd-btn-subtle" onClick={shuffle}>
                  <Shuffle size={16} />
                  Ver otro
                </button>
              )}
            </div>

            {projectLoading ? (
              <div className="dyd-skeleton" style={{ height: 300 }} />
            ) : (
              <article
                className={`dyd-panel dyd-spot-card ${spotShown ? 'dyd-rise' : 'is-hidden'}`}
                ref={spotRef}
                onMouseMove={trackPointer}
              >
                <div className="dyd-spot-media">
                  {hasCustomImage(project.projectImage) ? (
                    <img src={project.projectImage} alt={project.name} />
                  ) : (
                    <MediaPlaceholder name={project.name} variant="card" />
                  )}
                </div>

                <div className="dyd-spot-body">
                  {project.category && <span className="dyd-chip">{project.category}</span>}
                  <h3>{project.name}</h3>
                  <p>{truncateText(project.description, 230)}</p>

                  <div className="dyd-spot-actions">
                    <a className="dyd-btn dyd-btn-primary" href={`/project/${project._id}`}>
                      <Rocket size={16} />
                      Ver proyecto
                    </a>
                    {project.website && (
                      <a
                        className="dyd-btn dyd-btn-ghost"
                        href={getValidUrl(project.website)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Globe size={16} />
                        Visitar sitio
                      </a>
                    )}
                  </div>
                </div>
              </article>
            )}
          </div>
        </section>
      )}

      {/* ─────────── DENTRO DE LA PLATAFORMA ─────────── */}
      <section className="dyd-platform">
        <div
          className={`dyd-panel dyd-platform-panel ${platformShown ? 'dyd-rise' : 'is-hidden'}`}
          ref={platformRef}
          onMouseMove={trackPointer}
        >
          <div className="dyd-platform-copy">
            <span className="dyd-eyebrow">Dentro de la plataforma</span>
            <h2>
              Un club que cabe <span className="dyd-gradient-text">en tu bolsillo</span>
            </h2>
            <p className="dyd-lead">
              Todo lo que pasa en el club queda en un mismo sitio: quién es quién, qué se
              está construyendo y cuándo nos vemos.
            </p>
            <div className="dyd-platform-cta">
              <a href="/networking" className="dyd-btn dyd-btn-primary">
                Ver a los miembros
                <ArrowRight size={17} className="dyd-arrow" />
              </a>
              <a href="/contact" className="dyd-btn dyd-btn-ghost">
                Hablar con el club
              </a>
            </div>
          </div>

          <ul className="dyd-platform-list">
            {PLATFORM.map((item) => (
              <li key={item}>
                <span className="dyd-platform-check">
                  <Check size={13} strokeWidth={3} />
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <footer className="dyd-footer">
        <p>© {new Date().getFullYear()} DrivingYourDream Club</p>
      </footer>

      <style>{`
        .dyd-home { position: relative; }

        /* ── Hero ── */
        .dyd-hero {
          position: relative;
          min-height: 88vh;
          min-height: 88dvh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          padding: 90px 20px;
          margin-top: -1px;
        }
        .dyd-hero-bg {
          position: absolute;
          inset: -12% 0;
          background-size: cover;
          background-position: center;
          filter: brightness(0.32) saturate(1.15);
          will-change: transform;
        }
        .dyd-hero-veil {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(90% 70% at 50% 40%, rgba(7,9,13,0.25), rgba(7,9,13,0.85) 78%),
            linear-gradient(180deg, rgba(7,9,13,0.75) 0%, rgba(7,9,13,0.35) 35%, var(--dyd-bg) 100%);
        }
        /* Retícula técnica muy tenue, guiño a plano de ingeniería */
        .dyd-hero-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(59,130,246,0.055) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59,130,246,0.055) 1px, transparent 1px);
          background-size: 62px 62px;
          mask-image: radial-gradient(circle at 50% 45%, #000 0%, transparent 72%);
          -webkit-mask-image: radial-gradient(circle at 50% 45%, #000 0%, transparent 72%);
        }

        .dyd-hero-content {
          position: relative;
          z-index: 2;
          width: 100%;
          /* min() para que el bloque no se ensanche por encima de la pantalla:
             es un elemento flexible centrado y, sin esto, toma el ancho de su
             palabra mas larga (DRIVINGYOURDREAM) y el hero la recorta */
          max-width: min(920px, 100%);
          text-align: center;
        }
        .dyd-hero-content h1 {
          /* 8vw es lo que cabe con el titular en una linea a 320px */
          font-size: clamp(1.6rem, 8vw, 5rem);
          max-width: 100%;
          overflow-wrap: break-word;
          font-weight: 800;
          line-height: 1.02;
          letter-spacing: -0.045em;
          margin: 26px 0 22px;
          text-transform: uppercase;
          text-shadow: 0 18px 60px rgba(0,0,0,0.6);
        }
        .dyd-hero-content > p {
          font-size: clamp(1rem, 1.7vw, 1.2rem);
          color: #cbd5e1;
          max-width: 640px;
          margin: 0 auto 38px;
        }
        .dyd-hero-content > p strong { color: #fff; font-weight: 600; }

        .dyd-hero-cta {
          display: flex;
          flex-wrap: wrap;
          gap: 14px;
          justify-content: center;
        }
        .dyd-whatsapp {
          color: #04170c;
          background: linear-gradient(135deg, #25D366, #12a150);
          box-shadow: 0 10px 34px rgba(37,211,102,0.32);
        }
        .dyd-whatsapp:hover {
          color: #04170c;
          transform: translateY(-3px);
          box-shadow: 0 18px 46px rgba(37,211,102,0.5);
        }

        .dyd-scroll-hint {
          position: absolute;
          bottom: 30px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 2;
          color: var(--dyd-text-mute);
          animation: dyd-scroll-hint 2.4s ease-in-out infinite;
        }

        /* ── Ecosistema ── */
        .dyd-eco { padding: 96px 20px 110px; }
        .dyd-eco-inner { max-width: 1240px; margin: 0 auto; }
        .dyd-eco-head { text-align: center; margin-bottom: 56px; }
        .dyd-eco-head h2 {
          font-size: clamp(1.8rem, 4vw, 2.9rem);
          margin: 20px 0 0;
          letter-spacing: -0.035em;
        }

        .dyd-eco-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(100%, 258px), 1fr));
          gap: 24px;
        }
        .dyd-eco-card { text-decoration: none; color: inherit; }
        .dyd-eco-card.is-hidden { opacity: 0; }
        .dyd-eco-card .dyd-card-body { padding: 30px 26px; gap: 14px; }

        .dyd-eco-icon {
          display: grid;
          place-items: center;
          width: 56px;
          height: 56px;
          border-radius: 16px;
          margin-bottom: 6px;
          color: var(--dyd-blue-light);
          background: var(--dyd-accent-grad-soft);
          border: 1px solid rgba(59,130,246,0.26);
          transition: transform 0.45s var(--dyd-ease), color 0.45s var(--dyd-ease),
                      box-shadow 0.45s var(--dyd-ease);
        }
        .dyd-eco-card:hover .dyd-eco-icon {
          transform: translateY(-3px) rotate(-5deg);
          color: #fff;
          box-shadow: 0 12px 30px rgba(59,130,246,0.35);
        }

        .dyd-eco-link {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          margin-top: 6px;
          font-size: 0.78rem;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--dyd-blue-light);
        }
        .dyd-eco-card:hover .dyd-arrow { transform: translateX(5px); }
        .dyd-eco-link .dyd-arrow { transition: transform 0.3s var(--dyd-ease); }

        /* ── Conoce un proyecto ── */
        .dyd-spot { padding: 0 20px 96px; }
        .dyd-spot-inner { max-width: 1240px; margin: 0 auto; }
        .dyd-spot-head {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-end;
          justify-content: space-between;
          gap: 18px;
          margin-bottom: 30px;
        }
        .dyd-spot-head h2 {
          font-size: clamp(1.6rem, 3.2vw, 2.4rem);
          margin: 18px 0 0;
          letter-spacing: -0.035em;
        }

        .dyd-spot-card {
          display: grid;
          grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
          gap: 0;
          overflow: hidden;
        }
        .dyd-spot-card.is-hidden { opacity: 0; }

        .dyd-spot-media { position: relative; min-height: 300px; }
        .dyd-spot-media img,
        .dyd-spot-media .dyd-media-ph {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        /* Funde la imagen con el panel, que no quede un corte duro */
        .dyd-spot-media::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent 55%, rgba(17,22,31,0.55) 100%);
        }

        .dyd-spot-body {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 14px;
          padding: 40px 42px;
        }
        .dyd-spot-body h3,
        .dyd-spot-body p { max-width: 100%; overflow-wrap: break-word; }
        .dyd-spot-body h3 {
          font-size: clamp(1.3rem, 2.4vw, 1.75rem);
          font-weight: 600;
          letter-spacing: -0.025em;
          margin: 0;
        }
        .dyd-spot-body p {
          margin: 0;
          font-size: 0.97rem;
          line-height: 1.65;
          color: var(--dyd-text-dim);
        }
        .dyd-spot-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 10px;
        }

        /* ── Dentro de la plataforma ── */
        .dyd-platform { padding: 0 20px 110px; }
        .dyd-platform-panel {
          max-width: 1240px;
          margin: 0 auto;
          padding: 52px 48px;
          display: grid;
          grid-template-columns: 1.05fr 0.95fr;
          gap: 48px;
          align-items: center;
          overflow: hidden;
        }
        .dyd-platform-panel.is-hidden { opacity: 0; }
        /* Halo tenue alimentado por trackPointer (--mx / --my) */
        .dyd-platform-panel::after {
          content: '';
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: radial-gradient(
            420px circle at var(--mx, 50%) var(--my, 0%),
            rgba(59,130,246,0.14),
            transparent 68%
          );
        }
        .dyd-platform-copy { position: relative; z-index: 1; }
        .dyd-platform-copy h2 {
          font-size: clamp(1.6rem, 3.2vw, 2.4rem);
          margin: 20px 0 16px;
          letter-spacing: -0.035em;
        }
        .dyd-platform-cta {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 28px;
        }
        .dyd-platform-cta .dyd-arrow { transition: transform 0.3s var(--dyd-ease); }
        .dyd-platform-cta a:hover .dyd-arrow { transform: translateX(4px); }

        .dyd-platform-list {
          position: relative;
          z-index: 1;
          list-style: none;
          margin: 0;
          padding: 0;
          display: grid;
          gap: 14px;
        }
        .dyd-platform-list li {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          font-size: 0.94rem;
          line-height: 1.5;
          color: var(--dyd-text-dim);
          padding-bottom: 14px;
          border-bottom: 1px solid var(--dyd-border);
        }
        .dyd-platform-list li:last-child { padding-bottom: 0; border-bottom: none; }
        .dyd-platform-check {
          display: grid;
          place-items: center;
          flex-shrink: 0;
          width: 22px;
          height: 22px;
          margin-top: 1px;
          border-radius: 50%;
          color: var(--dyd-success);
          background: rgba(52,211,153,0.14);
          border: 1px solid rgba(52,211,153,0.3);
        }

        .dyd-footer {
          padding: 34px 20px;
          text-align: center;
          border-top: 1px solid var(--dyd-border);
        }
        .dyd-footer p {
          margin: 0;
          font-size: 0.84rem;
          letter-spacing: 0.08em;
          color: var(--dyd-text-mute);
        }

        /* Sin raton el fondo no se mueve: la retícula y el velo bastan */
        @media (max-width: 900px) {
          .dyd-hero-bg { transform: none !important; inset: 0; }
        }

        @media (max-width: 768px) {
          .dyd-hero { min-height: 82vh; min-height: 82dvh; padding: 70px 18px; }
          .dyd-hero-content h1 { margin: 20px 0 18px; }
          .dyd-hero-content > p { margin-bottom: 30px; }
          .dyd-hero-cta { flex-direction: column; }
          .dyd-hero-cta .dyd-btn { width: 100%; }
          .dyd-scroll-hint { bottom: 18px; }
          .dyd-eco { padding: 68px 18px 80px; }
          .dyd-eco-head { margin-bottom: 36px; }
          .dyd-eco-grid { gap: 16px; }
          .dyd-eco-card .dyd-card-body { padding: 24px 20px; gap: 12px; }
          .dyd-eco-icon { width: 48px; height: 48px; border-radius: 14px; }
          .dyd-spot { padding: 0 18px 66px; }
          .dyd-spot-head { margin-bottom: 22px; }
          .dyd-spot-card { grid-template-columns: 1fr; }
          .dyd-spot-media { min-height: 210px; }
          .dyd-spot-media::after {
            background: linear-gradient(180deg, transparent 50%, rgba(17,22,31,0.6) 100%);
          }
          .dyd-spot-body { padding: 26px 22px 30px; }
          .dyd-spot-actions { width: 100%; flex-direction: column; }
          .dyd-spot-actions .dyd-btn { width: 100%; }
          .dyd-platform { padding: 0 18px 76px; }
          .dyd-platform-panel {
            grid-template-columns: 1fr;
            gap: 34px;
            padding: 34px 24px;
          }
          .dyd-platform-cta { flex-direction: column; }
          .dyd-platform-cta .dyd-btn { width: 100%; }
        }

        @media (max-width: 420px) {
          .dyd-hero { padding: 56px 16px 72px; }
          .dyd-eco { padding: 54px 16px 62px; }
          .dyd-spot { padding: 0 16px 54px; }
          .dyd-spot-media { min-height: 178px; }
          .dyd-spot-body { padding: 22px 18px 26px; }
          .dyd-platform { padding: 0 16px 60px; }
          .dyd-platform-panel { padding: 28px 18px; gap: 28px; }
          .dyd-platform-list li { font-size: 0.9rem; }
          .dyd-footer { padding: 26px 16px; }
        }
      `}</style>
    </div>
  );
}

export default HomeComponent;
